import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck, Plus, Check, Loader2, ChevronDown, ChevronUp, Pencil, Rss,
  Youtube, MessageSquare, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';

const TRUST_STYLE = {
  official: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  mainstream: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  aggregator: 'bg-amber-50 text-amber-700 border-amber-200',
  community: 'bg-gray-50 text-gray-600 border-gray-200',
  unknown: 'bg-gray-50 text-gray-500 border-gray-200',
};
const ACCESS_LABEL = {
  official_api: 'Official API', rss: 'RSS', public_page: 'Public page',
  webhook: 'Webhook', manual: 'Manual',
  commercial_provider: 'Via commercial provider', future_connector: 'Coming soon',
};

// Trusted Sources: one-click connect the curated Signal Provider catalog
// (each connection stamped with its true origin), see honest availability
// for social platforms, and edit Source Intelligence Profiles — the
// tenant's own record of which origins they trust and how much.
const TrustedSourcesCard = () => {
  const [catalog, setCatalog] = useState(null);
  const [connections, setConnections] = useState([]);
  const [connecting, setConnecting] = useState('');
  const [profilesOpen, setProfilesOpen] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editForm, setEditForm] = useState({ type: 'mainstream', reliability: 50 });
  const [savingProfile, setSavingProfile] = useState(false);

  const load = useCallback(async () => {
    const [catRes, connRes] = await Promise.all([
      CommanServices.getSignalCatalog(),
      CommanServices.getApiConnections(),
    ]);
    const cat = catRes?.data || catRes;
    if (cat?.providers) setCatalog(cat);
    const conns = connRes?.data;
    if (Array.isArray(conns)) setConnections(conns);
  }, []);

  useEffect(() => { load(); }, [load]);

  const connectedUrls = useMemo(
    () => new Set(connections.map((c) => (c.base_url || '').trim())),
    [connections]
  );

  const connect = async (provider) => {
    if (connecting) return;
    setConnecting(provider.key);
    try {
      const res = await CommanServices.addApiConnection({
        name: provider.name,
        category: provider.category,
        base_url: provider.base_url,
        purposes: ['incident_reporting', 'notifications'],
        description: provider.description,
        origin_key: provider.origin_key,
        access_method: provider.access_method,
        is_active: true,
      });
      if (res?.status === false) { toast.error(res?.message || 'Could not connect'); return; }
      toast.success(`${provider.name} connected — signals flow into ForeSight`);
      load();
    } finally {
      setConnecting('');
    }
  };

  const startEdit = (key, profile) => {
    setEditKey(key);
    setEditForm({ type: profile.type || 'mainstream', reliability: profile.reliability ?? 50 });
  };

  const saveProfile = async () => {
    if (savingProfile || !editKey) return;
    setSavingProfile(true);
    try {
      const res = await CommanServices.upsertSourceProfile(editKey, {
        type: editForm.type, reliability: Number(editForm.reliability),
      });
      if (res?.status === false) { toast.error(res?.message || 'Save failed'); return; }
      toast.success('Source profile updated');
      setEditKey(null);
      load();
    } finally {
      setSavingProfile(false);
    }
  };

  if (!catalog) return null;

  const providers = catalog.providers || [];
  const curated = providers.filter((p) => p.available && p.base_url);
  const unavailable = providers.filter((p) => !p.available);
  const profiles = { ...(catalog.seed_profiles || {}) };
  Object.entries(catalog.tenant_profiles || {}).forEach(([k, v]) => {
    profiles[k] = { ...(profiles[k] || { key: k, display_name: k }), ...v };
  });

  const providerIcon = (p) => {
    if (p.access_method === 'rss' || p.access_method === 'public_page') return <Rss className="h-3.5 w-3.5 text-emerald-600" />;
    if (p.key === 'youtube') return <Youtube className="h-3.5 w-3.5 text-emerald-600" />;
    return <MessageSquare className="h-3.5 w-3.5 text-emerald-600" />;
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4" data-testid="trusted-sources-card">
      <p className="text-sm font-bold text-gray-800 flex items-center gap-1.5 mb-1">
        <ShieldCheck className="h-4 w-4 text-emerald-600" /> Trusted Sources
        <span className="text-[11px] font-semibold text-gray-400">Signal Provider catalog</span>
      </p>
      <p className="text-[11px] text-gray-500 mb-3">
        Curated origins ForeSight can trust — each connection is stamped with its true origin, so
        evidence credits the witness (SPA, Reuters…), never the transporter.
      </p>

      <div className="divide-y divide-gray-100">
        {curated.map((p) => {
          const connected = connectedUrls.has(p.base_url.trim());
          return (
            <div key={p.key} className="flex items-center gap-2 py-1.5" data-testid={`provider-${p.key}`}>
              {providerIcon(p)}
              <span className="text-[13px] font-semibold text-gray-800">{p.name}</span>
              <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${TRUST_STYLE[p.trust] || TRUST_STYLE.unknown}`}>
                {p.trust}
              </span>
              <span className="text-[10px] text-gray-400">{ACCESS_LABEL[p.access_method]}</span>
              {(p.languages || []).map((l) => (
                <span key={l} className="text-[10px] text-gray-400 uppercase">{l}</span>
              ))}
              {p.requires_key && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-semibold">
                  <Lock className="h-2.5 w-2.5" /> {p.requires_key}
                </span>
              )}
              <span className="ml-auto">
                {connected ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600" data-testid={`provider-connected-${p.key}`}>
                    <Check className="h-3.5 w-3.5" /> Connected
                  </span>
                ) : (
                  <button onClick={() => connect(p)} disabled={!!connecting} data-testid={`provider-connect-${p.key}`}
                    className="flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-bold px-2 py-1 hover:bg-emerald-100 disabled:opacity-60">
                    {connecting === p.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Connect
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {unavailable.length > 0 && (
        <p className="text-[11px] text-gray-400 mt-2" data-testid="providers-unavailable">
          {unavailable.map((p) => p.name).join(' · ')} — {ACCESS_LABEL.commercial_provider.toLowerCase()}
          (Dataminr, Meltwater, Brandwatch…); the architecture is ready when you are.
        </p>
      )}

      <button onClick={() => setProfilesOpen((v) => !v)} data-testid="profiles-toggle"
        className="mt-3 flex items-center gap-1 text-[11px] font-bold text-violet-700 hover:text-violet-900">
        {profilesOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        Source Intelligence Profiles ({Object.keys(profiles).length})
      </button>

      {profilesOpen && (
        <div className="mt-2 divide-y divide-gray-100" data-testid="profiles-list">
          {Object.values(profiles).sort((a, b) => (b.reliability || 0) - (a.reliability || 0)).map((prof) => (
            <div key={prof.key} className="flex items-center gap-2 py-1.5">
              <span className="text-[12px] font-semibold text-gray-700 w-52 truncate">{prof.display_name || prof.key}</span>
              {editKey === prof.key ? (
                <>
                  <select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                    className="text-[11px] border border-gray-300 rounded px-1.5 py-0.5 bg-white" data-testid="profile-type">
                    {(catalog.origin_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input type="number" min="0" max="100" value={editForm.reliability}
                    onChange={(e) => setEditForm({ ...editForm, reliability: e.target.value })}
                    className="w-16 text-[11px] border border-gray-300 rounded px-1.5 py-0.5 bg-white" data-testid="profile-reliability" />
                  <button onClick={saveProfile} disabled={savingProfile} data-testid="profile-save"
                    className="rounded bg-violet-600 text-white text-[10px] font-bold px-2 py-1 disabled:opacity-60">
                    {savingProfile ? <Loader2 className="h-3 w-3 animate-spin inline" /> : 'Save'}
                  </button>
                  <button onClick={() => setEditKey(null)} className="text-[10px] text-gray-400 font-semibold">Cancel</button>
                </>
              ) : (
                <>
                  <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${TRUST_STYLE[prof.type] || TRUST_STYLE.unknown}`}>
                    {prof.type}
                  </span>
                  <span className="text-[11px] text-gray-500 tabular-nums">
                    reliability {prof.reliability ?? '—'}
                    {prof.reliability_adjustment ? (
                      <span className={prof.reliability_adjustment > 0 ? 'text-emerald-600' : 'text-red-600'} title="Dynamic adjustment from track record">
                        {' '}({prof.reliability_adjustment > 0 ? '+' : ''}{prof.reliability_adjustment})
                      </span>
                    ) : null}
                  </span>
                  {(prof.historical_accuracy?.confirmed || prof.historical_accuracy?.contradicted) ? (
                    <span className="text-[10px] text-gray-400" title="Confirmed / contradicted by real outcomes">
                      {prof.historical_accuracy.confirmed || 0}✓ / {prof.historical_accuracy.contradicted || 0}✗
                    </span>
                  ) : null}
                  {prof.verification_required && <span className="text-[10px] text-amber-600 font-bold uppercase">verify</span>}
                  <button onClick={() => startEdit(prof.key, prof)} title="Edit profile" className="ml-auto"
                    data-testid={`profile-edit-${prof.key}`}>
                    <Pencil className="h-3 w-3 text-gray-400 hover:text-gray-700" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrustedSourcesCard;
